import { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "@pericles/framework-frontend";

import { fetchDiscoveryContext, type DiscoveryContextResponse } from "../api/projectDiscoveryContextClient";

interface ContextUsedBlockProps {
  projectId: string;
  refreshKey?: string | number;
  defaultOpen?: boolean;
}

function formatDateTime(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function ContextUsedBlock({ projectId, refreshKey, defaultOpen = false }: ContextUsedBlockProps): JSX.Element {
  const [state, setState] = useState<{
    data: DiscoveryContextResponse | null;
    isLoading: boolean;
    error: string | null;
  }>({
    data: null,
    isLoading: false,
    error: null,
  });
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const loadContext = useCallback(async () => {
    if (!projectId) return;
    setState((current) => ({ ...current, isLoading: true, error: null }));
    try {
      const response = await fetchDiscoveryContext(projectId);
      setState({ data: response, isLoading: false, error: null });
    } catch (err) {
      setState({ data: null, isLoading: false, error: err instanceof Error ? err.message : "Failed to load context" });
    }
  }, [projectId]);

  useEffect(() => {
    void loadContext();
  }, [loadContext, refreshKey]);

  const promptPreview = useMemo(() => {
    const lines = (state.data?.prompt ?? "").split(/\r?\n/);
    const previewLines = lines.slice(0, 20);
    const text = previewLines.join("\n").trim();
    const truncated = lines.length > previewLines.length;
    return { text, truncated };
  }, [state.data?.prompt]);

  const customer = state.data?.bundle.customer;
  const project = state.data?.bundle.project;
  const preDiscovery = state.data?.bundle.preDiscovery;

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-base font-semibold text-slate-900">Context used</div>
            <p className="text-sm text-slate-700">Snapshot of the discovery context sent to AI assistants.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="button button-secondary button-sm"
              onClick={() => void loadContext()}
              disabled={state.isLoading}
            >
              {state.isLoading ? "Loading…" : "Refresh"}
            </button>
            <button
              type="button"
              className="button button-link text-slate-700"
              onClick={() => setIsOpen((open) => !open)}
            >
              {isOpen ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {state.error ? <p className="text-sm text-red-600">{state.error}</p> : null}

        {isOpen ? (
          <div className="flex flex-col gap-3">
            {state.isLoading ? (
              <p className="text-sm text-slate-700">Loading context…</p>
            ) : (
              <>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded border border-slate-200 p-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Customer context</div>
                    <div className="text-sm font-semibold text-slate-900">{customer?.contextGenerationStatus ?? "unknown"}</div>
                    <div className="text-xs text-slate-600">
                      {customer?.contextGeneratedAt ? `Last generated ${formatDateTime(customer.contextGeneratedAt)}` : "Not generated"}
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 p-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Project context</div>
                    <div className="text-sm font-semibold text-slate-900">{project?.contextGenerationStatus ?? "unknown"}</div>
                    <div className="text-xs text-slate-600">
                      {project?.contextGeneratedAt ? `Last generated ${formatDateTime(project.contextGeneratedAt)}` : "Not generated"}
                    </div>
                  </div>
                  <div className="rounded border border-slate-200 p-3">
                    <div className="text-xs font-semibold uppercase text-slate-500">Pre-discovery</div>
                    <div className="text-sm font-semibold text-slate-900">{preDiscovery?.status ?? "unknown"}</div>
                    <div className="text-xs text-slate-600">
                      {preDiscovery?.generatedAt ? `Last generated ${formatDateTime(preDiscovery.generatedAt)}` : "Not generated"}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-1">
                  <div className="text-xs font-semibold uppercase text-slate-500">Discovery prompt (first 20 lines)</div>
                  <pre className="whitespace-pre-wrap rounded bg-slate-50 p-3 text-xs text-slate-800">
                    {promptPreview.text || "No prompt available."}
                    {promptPreview.truncated ? "\n…" : ""}
                  </pre>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
